// ft3d-hud/chat-commands.ts — the slash-command grammar for FT3D table chat (owner 2026-09-15).
//
// Pure: a typed line in, a send intent out. No DOM, no fetch — so the grammar can be reasoned about
// (and smoke-tested) on its own, and adding a command is one row in COMMANDS.
//
// The channel set and the voiced emote keys are the server's (includes/functions/chat/ft3d_chat_inc.php);
// this file only decides which one a typed line meant.

export type FtChatChannel = 'say' | 'yell' | 'emote' | 'ooc' | 'whisper' | 'gm';

export interface FtChatIntent {
    channel: FtChatChannel;
    text: string;
    /** A voiced emote key ('cheer', …). The SERVER authors its line, so `text` stays empty. */
    emote?: string;
    /** Whisper target, as typed. */
    target?: string;
}

export interface FtChatParse {
    intent?: FtChatIntent;
    /** Shown in the chat transcript as a local notice (never sent anywhere). */
    notice?: string;
    error?: string;
}

interface CommandSpec {
    channel: FtChatChannel;
    /** A voiced emote key for the /cheer family. */
    emote?: string;
    /** /w eats a name before its text. */
    takesTarget?: boolean;
    help: string;
}

/** Every alias → what it means. Order here is the order /help prints. */
export const COMMANDS: Record<string, CommandSpec> = {
    say: { channel: 'say', help: 'Speak in character to this encounter' },
    s: { channel: 'say', help: '' },
    yell: { channel: 'yell', help: 'Shout — louder, and it looks it' },
    y: { channel: 'yell', help: '' },
    shout: { channel: 'yell', help: '' },
    emote: { channel: 'emote', help: 'Describe an action: /emote draws her blade' },
    em: { channel: 'emote', help: '' },
    me: { channel: 'emote', help: '' },
    e: { channel: 'emote', help: '' },
    whisper: { channel: 'whisper', takesTarget: true, help: 'Private line: /w <name> <message>' },
    w: { channel: 'whisper', takesTarget: true, help: '' },
    tell: { channel: 'whisper', takesTarget: true, help: '' },
    t: { channel: 'whisper', takesTarget: true, help: '' },
    gm: { channel: 'gm', help: 'Message the GM only' },
    dm: { channel: 'gm', help: '' },
    ooc: { channel: 'ooc', help: 'Out of character, to the whole table' },
    o: { channel: 'ooc', help: '' },
};

/** Voiced/acted emotes: alias → the server emote key. A voice with no recorded take just stays silent. */
export const EMOTE_ALIASES: Record<string, string> = {
    cheer: 'cheer', hooray: 'cheer',
    taunt: 'taunt',
    point: 'point',
    follow: 'follow', comeon: 'follow',
    hold: 'hold', wait: 'hold',
    bye: 'bye', farewell: 'bye',
    laugh: 'laugh', lol: 'laugh',
    sigh: 'sigh', annoyed: 'sigh',
    no: 'no', cant: 'no',
    heal: 'heal', healme: 'heal',
    charge: 'charge', battlecry: 'charge',
    trap: 'trap',
    loot: 'loot',
    crit: 'crit', critical: 'crit',
    fumble: 'fumble', oops: 'fumble',
    levelup: 'levelup', ding: 'levelup',
    wave: 'wave', hello: 'wave', hi: 'wave', greet: 'wave',
    bow: 'bow',
    quickbow: 'quickbow',
    hattip: 'hattip', tip: 'hattip',
    clap: 'clap', applaud: 'clap',
    salute: 'salute',
    agree: 'agree',
    nod: 'nod', yes: 'nod',
    shrug: 'shrug',
    dance: 'dance', boogie: 'dance',
    // Second promotion batch (owner 2026-09-16).
    mock: 'mock', thisguy: 'mock',
    celebrate: 'celebrate', woohoo: 'celebrate',
    despair: 'despair', facepalm: 'despair',
    stomp: 'stomp',
    talk: 'talk',
    converse: 'converse', chatter: 'converse',
    listen: 'listen',
    sniff: 'sniff',
    stinky: 'stinky', smell: 'stinky',
    // Everyday life (owner 2026-10-09, the purchased Medieval NPC pack; lazy ft3d.avatar-clips.ambient.*). Silent.
    drink: 'drink', ale: 'drink',
    eat: 'eat',
    pray: 'pray',
    sleep: 'sleep', nap: 'sleep',
    sit: 'sit',
    sharpen: 'sharpen',
    hammer: 'hammer', forge: 'hammer',
    polish: 'polish',
    dust: 'dust',
    fan: 'fan',
    lean: 'lean',
    coin: 'coin', flip: 'coin',
    guard: 'guard',
    fidget: 'fidget', nervous: 'fidget',
    warm: 'warm',
    tend: 'tend', bandage: 'tend',
    scent: 'scent',
    strum: 'strum',
    stagger: 'stagger', drunk: 'stagger',
};

export interface FtChatLegendEntry {
    /** What typing this does, e.g. "/w". */
    command: string;
    label: string;
    description: string;
    /** Set for a channel the picker can switch to directly; emotes send immediately instead. */
    channel?: FtChatChannel;
    emote?: string;
    /** A whisper needs a name typed after it, so the picker prefills rather than sends. */
    needsTarget?: boolean;
}

/**
 * The picker/legend behind the channel chip: every channel and emote with what it actually does.
 * One source for the menu and for /help, so the two can never describe different commands.
 */
export function chatLegend(): { channels: FtChatLegendEntry[]; emotes: FtChatLegendEntry[] } {
    const channels: FtChatLegendEntry[] = Object.entries(COMMANDS)
        .filter(([, spec]) => spec.help !== '')
        .map(([name, spec]) => ({
            command: `/${name}`,
            label: channelLabel(spec.channel),
            description: spec.help,
            channel: spec.channel,
            needsTarget: spec.takesTarget === true,
        }));
    // One row per EMOTE, not per alias: /cheer and /hooray are the same line.
    const seen = new Set<string>();
    const emotes: FtChatLegendEntry[] = [];
    for (const [alias, emote] of Object.entries(EMOTE_ALIASES)) {
        if (seen.has(emote)) continue;
        seen.add(emote);
        emotes.push({
            command: `/${alias}`,
            label: alias.charAt(0).toUpperCase() + alias.slice(1),
            description: EMOTE_DESCRIPTIONS[emote] ?? 'Act it out',
            emote,
        });
    }
    return { channels, emotes };
}

/** What each emote does, in the player's words. Server twin: ft_chat_voiced_emotes()'s line text. */
const EMOTE_DESCRIPTIONS: Record<string, string> = {
    cheer: 'Cheer out loud',
    taunt: 'Taunt your enemies',
    point: 'Point something out',
    follow: 'Motion for everyone to follow',
    hold: 'Signal everyone to hold',
    bye: 'Bid farewell',
    laugh: 'Laugh',
    sigh: 'Sigh, annoyed',
    no: 'Shake your head',
    heal: 'Wave for healing until you move',
    charge: 'Let out a battle cry',
    trap: 'Warn of a trap',
    loot: 'Eye the loot',
    crit: 'Crow about a critical',
    fumble: 'Groan at a fumble',
    levelup: 'Celebrate levelling up',
    wave: 'Wave',
    bow: 'A formal bow',
    quickbow: 'A quick, informal bow',
    hattip: 'Tip your hat',
    clap: 'Applaud until you move',
    salute: 'Salute',
    agree: 'Nod along in agreement',
    nod: 'A firm nod',
    shrug: 'Shrug',
    dance: 'Dance your playlist until you move (set it in the action bar settings)',
    mock: 'Mock someone — get a load of this one',
    celebrate: 'Jump for joy until you move',
    despair: 'Clutch your head',
    stomp: 'Stomp in frustration',
    talk: 'Talk with your hands',
    converse: 'Chat away until you move',
    listen: 'Listen intently until you move',
    sniff: 'Sniff an armpit until you move',
    stinky: 'Waft away a foul smell until you move',
    drink: 'Take a long drink until you move',
    eat: 'Eat until you move',
    pray: 'Pray until you move',
    sleep: 'Doze off until you move',
    sit: 'Sit on the ground until you move',
    sharpen: 'Sharpen a blade until you move',
    hammer: 'Hammer at the forge until you move',
    polish: 'Polish your gear until you move',
    dust: 'Brush off the dust',
    fan: 'Fan yourself until you move',
    lean: 'Lean back until you move',
    coin: 'Flip a coin until you move',
    guard: 'Stand guard until you move',
    fidget: 'Fidget nervously until you move',
    warm: 'Warm your hands until you move',
    tend: 'Tend a wound until you move',
    scent: 'Sniff the air until you move',
    strum: 'Strum an imaginary lute',
    stagger: 'Stagger drunkenly until you move',
};

export function chatHelpText(): string {
    const lines = Object.entries(COMMANDS)
        .filter(([, spec]) => spec.help !== '')
        .map(([name, spec]) => `/${name} — ${spec.help}`);
    lines.push(`Emotes: ${Object.keys(EMOTE_ALIASES).map(a => `/${a}`).join(' ')}`);
    lines.push('Enter opens chat, Enter sends, Esc cancels, ↑ recalls what you typed.');
    return lines.join('\n');
}

/**
 * Split "<name> <message>" for /w. Quoted names win ("Delion Test" hi). Otherwise the LONGEST known
 * roster name that the line starts with, so multi-word names work without quotes; failing that, the
 * first word — which lets the server answer "no one here by that name" rather than guessing.
 */
function splitTarget(rest: string, names: string[]): { target: string; text: string } {
    const quoted = /^"([^"]+)"\s*(.*)$/s.exec(rest) ?? /^'([^']+)'\s*(.*)$/s.exec(rest);
    if (quoted) return { target: quoted[1]!.trim(), text: quoted[2]!.trim() };
    const lower = rest.toLowerCase();
    let best = '';
    for (const name of names) {
        const candidate = name.trim();
        if (!candidate || candidate.length <= best.length) continue;
        const next = lower.charAt(candidate.length);
        if (lower.startsWith(candidate.toLowerCase()) && (next === '' || next === ' ')) best = candidate;
    }
    if (best) return { target: best, text: rest.slice(best.length).trim() };
    const space = rest.indexOf(' ');
    return space < 0 ? { target: rest.trim(), text: '' } : { target: rest.slice(0, space), text: rest.slice(space + 1).trim() };
}

/**
 * Parse one typed line.
 *
 * @param raw          what the player typed
 * @param defaultChannel the channel a line with no slash uses (the bar's current channel)
 * @param names        roster names, so an unquoted multi-word whisper target resolves
 */
export function parseChatInput(
    raw: string,
    defaultChannel: FtChatChannel = 'say',
    names: string[] = [],
    defaultTarget = '',
): FtChatParse {
    const line = raw.replace(/\s+/g, ' ').trim();
    if (!line) return {};
    // A plain line rides the sticky channel — and a sticky WHISPER keeps pointing at the same person,
    // as WoW's does. Without a target to inherit it would be a whisper to nobody, so it falls back to say.
    const plain = (text: string): FtChatParse => {
        if (defaultChannel !== 'whisper') return { intent: { channel: defaultChannel, text } };
        // ⛔ Never recover a broken PRIVATE state by speaking publicly. A sticky whisper with no
        // target left is an error the player must resolve, not a line to say out loud.
        return defaultTarget
            ? { intent: { channel: 'whisper', text, target: defaultTarget } }
            : { error: 'Whisper to whom? Use /w <name> <message>, or /say to speak aloud.' };
    };
    if (!line.startsWith('/')) return plain(line);
    // "//text" is an escape hatch for a line that genuinely starts with a slash.
    if (line.startsWith('//')) return plain(line.slice(1));

    const match = /^\/([a-z?]+)\s*(.*)$/is.exec(line);
    if (!match) return { error: 'That is not a command. Try /help.' };
    const name = match[1]!.toLowerCase();
    const rest = match[2]!.trim();

    if (name === 'help' || name === '?' || name === 'commands') return { notice: chatHelpText() };

    // Own-property check, not a bare lookup: `/constructor` and friends would otherwise resolve
    // through the prototype and be treated as a real command (a `Record<>` type is no runtime
    // protection). hasOwnProperty.call, because the bundle's lib target predates Object.hasOwn.
    const own = (table: object, key: string): boolean => Object.prototype.hasOwnProperty.call(table, key);
    const emote = own(EMOTE_ALIASES, name) ? EMOTE_ALIASES[name] : undefined;
    if (emote) return { intent: { channel: 'emote', text: '', emote } };

    const spec = own(COMMANDS, name) ? COMMANDS[name] : undefined;
    if (!spec) return { error: `Unknown command /${name}. Try /help.` };

    if (spec.takesTarget) {
        const { target, text } = splitTarget(rest, names);
        if (!target) return { error: 'Whisper to whom? /w <name> <message>' };
        if (!text) return { error: `What do you want to whisper to ${target}?` };
        return { intent: { channel: spec.channel, text, target } };
    }
    if (!rest) return { error: `Type something after /${name}.` };
    return { intent: { channel: spec.channel, text: rest } };
}

/** The label shown left of the input for the channel currently being typed into. */
export function channelLabel(channel: FtChatChannel, target?: string): string {
    switch (channel) {
        case 'yell': return 'Yell';
        case 'emote': return 'Emote';
        case 'ooc': return 'OOC';
        case 'gm': return 'To GM';
        case 'whisper': return target ? `To ${target}` : 'Whisper';
        default: return 'Say';
    }
}
